/** Pure player-line presentation for a tactical Pitch node. */
import { GripVerticalIcon, RefreshCwIcon, XIcon } from "lucide-react-native";
import React, { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Button } from "@/components/atoms/Button";
import { Dot } from "@/components/atoms/Dot";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import PitchAssignmentSelect from "@/features/scouting/components/molecules/PitchAssignmentSelect";
import {
  PITCH_NAME_LINK_FOCUS_CLASS,
  PITCH_NAME_LINK_TEXT_CLASS,
} from "@/features/scouting/constants/pitchNameLink";

import type {
  PitchMemberRemoveState,
  PitchPositionPlayerLineProps,
} from "./PitchPositionPlayerLineContainer";

/** Icon tone per removal state; the chip has no room for a status line. */
const REMOVE_TONE: Readonly<
  Record<PitchMemberRemoveState | "idle", "muted" | "danger" | "warning">
> = {
  idle: "muted",
  removing: "muted",
  failed: "danger",
  conflict: "warning",
};

/**
 * The chip's accessible name carries what the panel row says in its status
 * line: Retry after a refusal, "Scenario changed" after a conflict.
 * @param name - The player's display name
 * @param state - Where the removal stands
 * @returns The accessibility label
 */
const removeLabel = (name: string, state?: PitchMemberRemoveState): string =>
  state === "failed"
    ? `Retry removing ${name} from the scenario`
    : state === "conflict"
      ? `Scenario changed — remove ${name} from the scenario again`
      : `Remove ${name} from the scenario`;

/** Prepared props for the pure player line. */
export interface PitchPositionPlayerLineViewProps extends Omit<
  PitchPositionPlayerLineProps,
  "onRemove"
> {
  readonly nodeRef: (node: HTMLElement | null) => void;
  readonly dragBindings: Partial<React.ComponentProps<typeof Pressable>>;
  /** Bound Remove for this line (SE-8353); absent = no affordance. */
  readonly onRemove?: () => void;
  /** Opens this player's page (SE-8605); absent = the name is plain text. */
  readonly onOpenPlayer?: () => void;
}

/** The ○ target marker: a 1px content-subtle ring (Figma 1010:20293). */
const TARGET_MARKER_CLASS = "border border-content-subtle bg-transparent";

/**
 * SE-8535: hidden at rest, shown while the line is hovered or holds keyboard
 * focus (the RowActionsCell pattern). Still focusable, so keyboard users
 * reach it.
 */
const REVEAL_ON_HOVER =
  "web:opacity-0 web:group-hover:opacity-100 web:group-focus-within:opacity-100";

/** The 12 px grip keeps a 20 px pointer target. */
const GRIP_HIT_SLOP = { top: 4, bottom: 4, left: 4, right: 4 } as const;

/**
 * Pure field-player line with pointer and explicit keyboard controls.
 * @param root0 - Prepared field player presentation.
 * @param root0.player - Authorized player data.
 * @param root0.isPrimary - Whether the player leads the slot depth order.
 * @param root0.slot - Slot currently containing the player.
 * @param root0.canAssign - Whether write controls are visible.
 * @param root0.assignmentSlots - Every slot in the current formation.
 * @param root0.onAssign - Handles explicit Select assignment.
 * @param root0.nodeRef - DnD source node ref.
 * @param root0.dragBindings - DnD activator attributes and listeners.
 * @param root0.onRemove - Bound Remove, when the scenario Pitch offers one.
 * @param root0.removeState - Where that removal stands; idle when omitted.
 * @param root0.onOpenPlayer - Opens the player page from the name, when offered.
 */
const PitchPositionPlayerLineView = ({
  player,
  isPrimary,
  slot,
  canAssign,
  assignmentSlots,
  onAssign,
  nodeRef,
  dragBindings,
  onRemove,
  removeState,
  onOpenPlayer,
}: PitchPositionPlayerLineViewProps) => (
  <Stack
    ref={nodeRef as never}
    testID={`pitch-player-${player.playerId}`}
    direction="horizontal"
    // SE-8535: `group` so the grip and chevron reveal on hover / focus-within;
    // `relative` anchors the chevron over the line's end.
    className="group relative w-full items-center gap-1"
  >
    {canAssign ? (
      // SE-8535 (Figma 281:2793): at rest the line is ○ + name. The grip sits
      // over the marker and shows on hover or keyboard focus, taking no width.
      <Box className="relative size-3 items-center justify-center">
        <Dot
          testID={`pitch-line-marker-${player.playerId}`}
          className={`${TARGET_MARKER_CLASS} web:group-hover:opacity-0 web:group-focus-within:opacity-0`}
        />
        <Pressable
          {...dragBindings}
          testID={`pitch-player-drag-${player.playerId}`}
          accessibilityRole="button"
          accessibilityLabel={`Move ${player.displayName} from ${slot}`}
          hitSlop={GRIP_HIT_SLOP}
          className={`absolute inset-0 touch-none items-center justify-center rounded-sm focus-visible:ring-2 focus-visible:ring-outline-default ${REVEAL_ON_HOVER}`}
        >
          <Icon
            as={GripVerticalIcon}
            size="xs"
            className="text-content-muted"
          />
        </Pressable>
      </Box>
    ) : (
      // PitchPlayerLine kind=target: ○, a 1px content-subtle ring
      // (Figma 1010:20293); ● accent is reserved for squad players.
      <Dot
        testID={`pitch-line-marker-${player.playerId}`}
        className={TARGET_MARKER_CLASS}
      />
    )}
    {onOpenPlayer ? (
      // SE-8605: the name opens the player page, as the Table view's does.
      // Only the name: the grip keeps the drag and the chevron the assignment.
      <Pressable
        testID={`pitch-name-link-target-${player.playerId}`}
        accessibilityRole="link"
        onPress={onOpenPlayer}
        // The link fills the line's flexible middle, as the name did.
        className={`min-w-0 flex-1 ${PITCH_NAME_LINK_FOCUS_CLASS}`}
      >
        <Text
          variant={isPrimary ? "caption-strong" : "caption"}
          className={`text-content-primary ${PITCH_NAME_LINK_TEXT_CLASS}`}
          isTruncated
        >
          {player.displayName}
        </Text>
      </Pressable>
    ) : (
      <Text
        variant={isPrimary ? "caption-strong" : "caption"}
        className="min-w-0 flex-1 text-content-primary"
        isTruncated
      >
        {player.displayName}
      </Text>
    )}
    {canAssign ? (
      // SE-8535: the chevron overlays the line's end on hover or focus
      // instead of reserving ~20 px the name needs.
      <Box
        testID={`pitch-player-assign-${player.playerId}`}
        // With Remove at the line's end (scenario Pitch) the chevron stays in
        // flow, so it never covers Remove.
        className={
          onRemove
            ? REVEAL_ON_HOVER
            : `absolute right-0 bg-surface-base ${REVEAL_ON_HOVER}`
        }
      >
        <PitchAssignmentSelect
          playerId={player.playerId}
          surface="pitch"
          playerName={player.displayName}
          value={slot}
          slots={assignmentSlots}
          onAssign={onAssign}
        />
      </Box>
    ) : null}
    {onRemove ? (
      <Button
        testID={`scenario-remove-pitch-${player.playerId}`}
        icon={removeState === "failed" ? RefreshCwIcon : XIcon}
        iconOnly
        accessibilityLabel={removeLabel(player.displayName, removeState)}
        variant="link"
        size="xs"
        iconSize="sm"
        iconTone={REMOVE_TONE[removeState ?? "idle"]}
        onPress={onRemove}
        isLoading={removeState === "removing"}
        isDisabled={removeState === "removing"}
      />
    ) : null}
  </Stack>
);

PitchPositionPlayerLineView.displayName = "PitchPositionPlayerLineView";
export default memo(PitchPositionPlayerLineView);
